'use strict';

import Flickity from 'flickity';

document.addEventListener(
  'DOMContentLoaded',
  function() {
    const nav = document.querySelector<HTMLElement>('#js-nav') as HTMLElement;
    if (!nav) {
      return;
    }

    const navBp: number = 1090;
    // Helper to centralize mobile breakpoint checks
    function isMobile(): boolean {
      return window.matchMedia(`(max-width: ${navBp - 1}px)`).matches;
    }

    const toggleBtn = nav.querySelector('.js-toggle-nav');
    const mainNavList = nav.querySelector<HTMLElement>('.nav__list--level-1') as HTMLElement;
    const navDropdownLinks = nav.querySelectorAll<HTMLAnchorElement>('a[data-dropdown="true"]');
    const navLinksAll = nav.querySelectorAll('a, button');
    const mobileNavMenu = nav.querySelector('.js-mobile-nav');
    const backBtns = nav.querySelectorAll('.js-nav-back');
    const announcementBanner = document.querySelector('.js-announcement-banner');
    const navAccordionButtons = nav.querySelectorAll<HTMLButtonElement>('.nav__accordion-button');
    const navScrim = nav.querySelector<HTMLElement>('.js-nav-scrim');

    const searchBtns = [].slice.call(document.querySelectorAll('.js-nav-search'));
    const searchBox = document.querySelector<HTMLElement>('.js-search-box') as HTMLElement;
    const searchInputs = searchBox ? [].slice.call(searchBox.querySelectorAll('a, button, input')) : [];
    const searchCloseBtn = searchBox ? searchBox.querySelector('.js-search-close') : undefined;
    let clickedSearchBtn: any;

    /* -----------
		* Functions
		----------- */

    /* --- Toggle mobile nav open and closed --- */
    function toggleNav() {
      if (nav.classList.contains('open')) {
        // close (this re-enables body scroll)
        closeMobileNav();
      } else {
        // open — .nav.open pins the nav to the viewport (see nav.less), so the
        // menu can open in place from any scroll position without scrolling first
        closeSearchBox();
        closeSubMenus();
        closeAccordions();
        nav.classList.add('open');

        const body = document.querySelector('body');
        if (body) {
          body.style.overflow = 'hidden';
        }
      }
    }

    /* --- Close mobile nav --- */
    function closeMobileNav() {
      closeSearchBox();
      nav.classList.remove('open');

      // console.debug('Enabling vanilla body scroll');
      const body = document.querySelector('body');
      if (body) {
        body.style.overflow = '';
      }

      // reset any sub menus that were opened
      setTimeout(function() {
        closeSubMenus();
        closeAccordions();
      }, 300); // set the time to be the same as the css animation to close the nav
    }

    /* --- Close submenus if focusing on a different nav link --- */
    function closeSubMenusOnFocus() {
      const openDropdown = document.querySelector('.nav__dropdown.open');
      const linkInDropdown = openDropdown && openDropdown.contains(this) ? true : false;
      const linkInSearch = searchBox?.contains(this) ? true : false;
      const mobileOpenSearchBtn = this.classList.contains('js-nav-search') && isMobile() ? true : false;
      if (!this.classList.contains('open') && !linkInDropdown && !linkInSearch && !mobileOpenSearchBtn) {
        closeSubMenus();
      }
    }

    /* --- Open the submenu for the selected link (mobile & desktop) --- */
    function openSubMenu(link: HTMLElement) {
      const dropdown = link.nextElementSibling as HTMLElement | null;
      if (!dropdown?.classList.contains('nav__dropdown')) return;
      closeSubMenus();

      // A department click dismisses search before opening its panel.
      if (!isMobile()) {
        closeSearchBox();
      }

      const menuContainer = mobileNavMenu?.querySelector('.nav__menu-container');
      link.classList.add('open');
      link.setAttribute('aria-expanded', 'true');
      mobileNavMenu?.classList.add('sub-menu-open');
      dropdown.classList.add('open');
      dropdown.setAttribute('aria-hidden', 'false');
      dropdown.scrollTop = 0;

      if (!isMobile()) {
        navScrim?.classList.add('open');
      }

      menuContainer?.scrollTo({ top: 0, behavior: 'smooth' });
    }

    /* --- Close the open submenus (mobile & desktop) --- */
    function closeSubMenus() {
      const links = nav.querySelectorAll('.nav__link.open');
      const dropdowns = nav.querySelectorAll('.nav__dropdown.open');

      mobileNavMenu?.classList.remove('sub-menu-open');

      links.forEach((link) => {
        link.classList.remove('open');
        link.setAttribute('aria-expanded', 'false');
      });
      dropdowns.forEach((dropdown) => {
        dropdown.classList.remove('open');
        dropdown.setAttribute('aria-hidden', 'true');
      });
      navScrim?.classList.remove('open');
    }

    /* --- Toggle sub menus open and closed (mobile & desktop) --- */
    function toggleSubMenu(e) {
      e.preventDefault();

      if (!this.classList.contains('open')) {
        openSubMenu(this);
      } else {
        closeSubMenus();
      }
    }

    /* --- Open the search box --- */
    function openSearchBox() {
      if (!searchBox) {
        return;
      }

      // Last interaction wins: opening the search dismisses any open dropdown
      closeSubMenus();

      if (nav.classList.contains('open')) {
        searchBox.style.maxHeight = 'calc(var(--vh, 1vh) * 100 - var(--nav-sticky-offset, 6.4rem))';
      } else {
        // Generous cap rather than a snapshot of scrollHeight — the suggestions
        // panel grows the box after opening (12 hits + "Show all results")
        searchBox.style.maxHeight = 'calc(var(--vh, 1vh) * 100 - var(--nav-sticky-offset, 6.4rem))';
      }
      searchBox.setAttribute('aria-hidden', 'false');
      searchInputs.forEach((input) => {
        input.setAttribute('tabindex', '0');
      });
      // Pin the nav visible with NO transform while the box is open — the box
      // is position:fixed inside the nav, and a transformed ancestor (the
      // .fixed.show reveal state) hijacks fixed positioning off-screen
      nav.classList.add('nav--search-open');
      // Anchor precisely below the header as it is RIGHT NOW
      setNavStickyOffset();
      // Lock page scroll (as the mobile menu does): focusing the input lets
      // the browser scroll the page, sliding the box under the sticky header
      document.body.style.overflow = 'hidden';
      // Desktop only: auto-focus for immediate typing. On mobile the focus
      // summons the keyboard and iOS scrolls the box's CONTENT under the
      // header — the visitor taps the field themselves instead.
      if (window.matchMedia('(min-width: 1090px)').matches) {
        searchBox.querySelector<HTMLElement>('.aa-Input')?.focus();
      }
      searchBox.scrollTop = 0;
      clickedSearchBtn = this;
    }

    /* --- The box itself must never scroll (its inner .nav__s-scroll region
       handles scrolling): browsers scroll even overflow:hidden ancestors to
       reveal a focused field, which kept pushing the input under the header.
       Event-driven reset — reacts to every scroll, no timers to lose. --- */
    if (searchBox) {
      searchBox.addEventListener('scroll', () => {
        if (searchBox.scrollTop !== 0) searchBox.scrollTop = 0;
        if (searchBox.scrollLeft !== 0) searchBox.scrollLeft = 0;
      });
    }

    /* --- Close the search box --- */
    function closeSearchBox() {
      if (!searchBox) {
        return;
      }

      searchBox.style.maxHeight = '';
      searchBox.setAttribute('aria-hidden', 'true');
      searchInputs.forEach((input) => {
        input.setAttribute('tabindex', '-1');
      });
      nav.classList.remove('nav--search-open');
      setNavStickyOffset();
      // Re-enable page scroll unless the mobile menu (which manages its own
      // lock) is the one closing us
      if (!nav.classList.contains('open')) {
        document.body.style.overflow = '';
      }
      if (clickedSearchBtn) {
        clickedSearchBtn.focus();
        clickedSearchBtn = undefined;
      }
    }

    /* --- Resize the search box when screen size changes --- */
    function resizeSearchBox() {
      if (searchBox && searchBox.getAttribute('aria-hidden') === 'false') {
        if (!nav.classList.contains('open')) {
          searchBox.style.maxHeight = 'calc(var(--vh, 1vh) * 100 - var(--nav-sticky-offset, 6.4rem))';
        }
      }
    }
    /* --- Save the height of the header to a CSS variable --- */
    function saveHeaderHeight() {
      // Nav bar only (no announcement banner) — used to flush-align sticky content with
      // the nav when it re-reveals on scroll-up (the banner is gone by then).
      const navHeight = nav.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--nav-height', navHeight + 'px');

      let headerHeight = navHeight;
      if (announcementBanner) {
        const announcementBannerHeight = announcementBanner.getBoundingClientRect().height;
        headerHeight += announcementBannerHeight;
      }
      document.documentElement.style.setProperty('--header-height', headerHeight + 'px');
    }

    // The nav's current visible bottom edge (0 when it's scrolled out of view). Used by
    // sticky page content to sit flush against the nav whatever state it's in.
    function setNavStickyOffset() {
      const navBottom = Math.max(0, nav.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty('--nav-sticky-offset', navBottom + 'px');
    }

    /* --- Nav accordions (mobile only) --- */
    function closeAccordions() {
      // target only lists that live inside a nav__accordion
      const openPanels = nav.querySelectorAll<HTMLElement>('.nav__accordion .nav__list.open');
      openPanels.forEach((panel) => {
        const parent = panel.closest('.nav__accordion');
        const btn = parent?.querySelector<HTMLElement>('.nav__accordion-button');
        // update button state immediately
        btn?.classList.remove('open');
        btn?.setAttribute('aria-expanded', 'false');

        // animate collapse: set explicit height first, then set to 0 to trigger transition
        panel.style.maxHeight = panel.scrollHeight + 'px';
        // force next frame then collapse
        requestAnimationFrame(() => {
          panel.style.maxHeight = '0';
        });

        // remove open state after animation completes
        panel.addEventListener(
          'transitionend',
          () => {
            panel.classList.remove('open');
            panel.setAttribute('aria-hidden', 'true');
            // keep maxHeight at 0 after closed so subsequent opens animate correctly
            panel.style.maxHeight = '0';
          },
          { once: true }
        );
      });
    }

    function resetAccordionsForDesktop() {
      // On desktop: ensure panels are visible as normal lists (remove accordion inline styles)
      // On mobile: collapse panels by default
      const isDesktop = !isMobile();
      navAccordionButtons.forEach((btn) => {
        const parent = btn.closest('.nav__accordion');
        const panel = parent?.querySelector<HTMLElement>('.nav__accordion-panel')
          ?? parent?.querySelector<HTMLElement>('.ais-Panel-body')
          ?? parent?.querySelector<HTMLElement>('.nav__accordion-panel');

        if (isDesktop) {
          // make list behave like normal list
          btn.classList.remove('open');
          btn.setAttribute('aria-expanded', 'false');
          if (panel) {
            panel.style.maxHeight = '';
            panel.classList.remove('open');
            panel.setAttribute('aria-hidden', 'false');
          }
        } else {
          // mobile: collapse panels by default
          btn.classList.remove('open');
          btn.setAttribute('aria-expanded', 'false');
          if (panel) {
            panel.style.maxHeight = '0';
            panel.classList.remove('open');
            panel.setAttribute('aria-hidden', 'true');
          }
        }
      });
    }

    function initNavAccordions() {
      navAccordionButtons.forEach((btn) => {
        const parent = btn.closest('.nav__accordion');
        const panel = parent?.querySelector<HTMLElement>('.nav__accordion-panel');

        // initialize states based on viewport
        if (panel) {
          panel.setAttribute('aria-hidden', !isMobile() ? 'false' : 'true');
          panel.style.maxHeight = !isMobile() ? '' : '0';
        }

        btn.addEventListener('click', (e) => {
          // behave as accordion only on mobile widths
          if (!isMobile()) {
            return;
          }
          e.preventDefault();

          if (!panel) {
            return;
          }

          if (panel.classList.contains('open')) {
            // collapse the panel (animated)
            btn.classList.remove('open');
            btn.setAttribute('aria-expanded', 'false');

            panel.style.maxHeight = '';
            panel.classList.remove('open');
            panel.setAttribute('aria-hidden', 'true');
            // requestAnimationFrame(() => {
            //   panel.style.maxHeight = '0';
            // });

            // panel.addEventListener(
            //   'transitionend',
            //   () => {
            //     panel.classList.remove('open');
            //     panel.setAttribute('aria-hidden', 'true');
            //     panel.style.maxHeight = '0';
            //   },
            //   { once: true }
            // );
          } else {
            // close other nav accordions first (optional: keep single open)
            closeAccordions();
            btn.classList.add('open');
            btn.setAttribute('aria-expanded', 'true');

            // expand (animated) and clear inline maxHeight after transition so the panel can resize naturally
            panel.style.maxHeight = `${panel.scrollHeight}px`;
            panel.classList.add('open');
            panel.setAttribute('aria-hidden', 'false');

            // panel.addEventListener(
            //   'transitionend',
            //   () => {
            //     // allow content to determine height after opening
            //     panel.style.maxHeight = '';
            //   },
            //   { once: true }
            // );
          }
        });
      });
    }

    // Init announcements banner Flickity
    if (announcementBanner) {
      const announcementBannerFlickity = new Flickity(announcementBanner, {
        cellSelector: '.announcement-banner__slide',
        wrapAround: true,
        cellAlign: 'center',
        prevNextButtons: false,
        pageDots: false,
        autoPlay: 5000,
        selectedAttraction: 0.012,
      });

      // Bind arrows
      const prevBtn = announcementBanner.querySelector('.js-prev');
      const nextBtn = announcementBanner.querySelector('.js-next');
      prevBtn?.addEventListener('click', () => announcementBannerFlickity.previous());
      nextBtn?.addEventListener('click', () => announcementBannerFlickity.next());
    }

    /* Event listeners */
    toggleBtn?.addEventListener('click', toggleNav); // mobile nav toggle menu button

    // ensure accordions are initialized for nav
    initNavAccordions();

    navDropdownLinks.forEach((dropdownLink) => {
      // Click (including native Enter activation) toggles on desktop and mobile.
      dropdownLink.setAttribute('aria-expanded', 'false');
      dropdownLink.addEventListener('click', toggleSubMenu);
      dropdownLink.addEventListener('keydown', (e: KeyboardEvent) => {
        // ArrowDown from a department link opens its panel and enters the first link
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (!dropdownLink.classList.contains('open')) {
            openSubMenu(dropdownLink);
          }
          const dropdown = dropdownLink.nextElementSibling as HTMLElement | null;
          dropdown?.querySelector<HTMLElement>('a, button, input')?.focus();
        }
      });
    });

    // Scrim click closes the open desktop panel
    navScrim?.addEventListener('click', closeSubMenus);

    // The × in a mobile panel toolbar closes the whole drawer
    nav.querySelectorAll('.js-nav-close-drawer').forEach((btn) => {
      btn.addEventListener('click', closeMobileNav);
    });

    // Keep the panel open while the pointer moves; close only on an outside click.
    document.addEventListener('click', (e) => {
      const target = e.target as Node;
      if (!mainNavList.contains(target) && !isMobile()) {
        closeSubMenus();
      }
    });

    // Escape closes an open panel and returns focus to its department link
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }
      const openLink = nav.querySelector<HTMLElement>('.nav__link.open');
      if (openLink) {
        closeSubMenus();
        openLink.focus();
      }
    });

    navLinksAll.forEach((link) => {
      // all links in the nav
      link.addEventListener('focusin', closeSubMenusOnFocus);
    });

    backBtns.forEach((backBtn) => {
      // mobile 'back' buttons
      backBtn.addEventListener('click', closeSubMenus);
    });

    searchBtns.forEach((searchBtn) => {
      // toggle search buttons
      searchBtn.addEventListener('click', () => {
        const isOpen = searchBox.getAttribute('aria-hidden') === 'false';
        if (isOpen) {
          closeSearchBox();
        } else {
          openSearchBox();
        }
      });
    });

    searchCloseBtn?.addEventListener('click', closeSearchBox); // close search box

    window.addEventListener('resize', () => {
      if (!isMobile()) {
        closeMobileNav();
        // reset any sub menus that were opened
        closeSubMenus();
      }
      // Accordion behavior must adapt to viewport
      resetAccordionsForDesktop();
      resizeSearchBox();
      saveHeaderHeight();
    });

    // Ensure accordions are normalized on first load
    resetAccordionsForDesktop();

    /* --- Sticky nav, showing on scroll up (mobile & desktop) --- */
    let previousPosition = -1;

    window.addEventListener('scroll', () => {
      const windowPosition = window.scrollY || window.pageYOffset;
      const windowHeight = window.innerHeight;
      let startPosition: number = 0;

      if (announcementBanner) {
        let announcementBannerBox = announcementBanner.getBoundingClientRect();
        startPosition = announcementBannerBox.height;
      }

      if (!isMobile()) {
        startPosition += nav.getBoundingClientRect().height - mobileNavMenu!.getBoundingClientRect().height;
      }

      if (windowPosition > windowHeight) {
        nav.classList.add('fixed');
        nav.classList.remove('top');

        if (windowPosition < previousPosition) {
          nav.classList.add('show');
          document.body.classList.add('body--nav-sticky');
        } else {
          nav.classList.remove('show');
          document.body.classList.remove('body--nav-sticky');
        }
      } else if (windowPosition <= startPosition) {
        nav.classList.remove('show');
        document.body.classList.remove('nav-sticky');
        nav.classList.remove('fixed');
        nav.classList.remove('top');
      } else if (windowPosition < windowHeight) {
        nav.classList.add('top');
      }

      // Expose the nav's actual visible bottom edge so sticky page content (e.g. the
      // collection title bar) can flush-align to it in every state/breakpoint, including
      // the compressed nav shown on scroll-up.
      setNavStickyOffset();

      previousPosition = windowPosition;
    });

    // Keep the offset accurate while the nav animates in/out (transform transition).
    nav.addEventListener('transitionend', setNavStickyOffset);
    setNavStickyOffset();
  }
);
